import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Instagram, Youtube, Facebook, Music2, KeyRound } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { RoyalListForm } from "../components/RoyalListForm";
import { useSEO } from "../lib/useSEO";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function ArchiveLanding() {
  const site = useSite();
  const bgImg = site.landing_image || "";

  useSEO(
    "A New Chapter Is Coming",
    "Queen Denae is preparing the next chapter of the Born Soulful era. New music. New stories. A new experience."
  );

  useEffect(() => {
    let el = document.head.querySelector('meta[name="robots"]');
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.setAttribute("content", "noindex, nofollow");
    return () => el.setAttribute("content", "index, follow");
  }, []);

  const socials = [
    { label: "Instagram", icon: Instagram, href: site.social_instagram, testid: "landing-social-instagram" },
    { label: "YouTube", icon: Youtube, href: site.social_youtube, testid: "landing-social-youtube" },
    { label: "Facebook", icon: Facebook, href: site.social_facebook, testid: "landing-social-facebook" },
    { label: "TikTok", icon: Music2, href: site.social_tiktok, testid: "landing-social-tiktok" },
  ].filter((s) => s.href);

  return (
    <div data-testid="archive-landing" className="min-h-screen relative flex flex-col bg-obsidian overflow-hidden">
      {bgImg && (
        <div className="absolute inset-0">
          <img src={bgImg} alt="" className="w-full h-full object-cover object-top animate-slow-zoom opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian/80 via-obsidian/60 to-obsidian" />
        </div>
      )}

      <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-5 py-20 sm:py-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="font-mono text-[11px] tracking-[0.45em] uppercase text-goldglow"
        >
          The Born Soulful Era
        </motion.p>

        {site.landing_portrait && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 border border-gold/30 p-2 bg-obsidian/40 shadow-2xl shadow-black/60"
            data-testid="landing-portrait"
          >
            <img
              src={site.landing_portrait}
              alt="Queen Denae"
              className="w-56 sm:w-72 aspect-[3/4] object-cover object-top"
            />
          </motion.div>
        )}

        <h1 className="mt-6 font-serif tracking-tight leading-[0.9] text-cream text-[16vw] sm:text-[11vw] lg:text-[8rem]" data-testid="landing-headline">
          <MaskedLine delay={0.5}>QUEEN</MaskedLine>
          <MaskedLine delay={0.68} className="italic text-goldglow">DENAE</MaskedLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-8 max-w-xl"
        >
          <p className="font-serif italic text-2xl sm:text-3xl text-goldglow" data-testid="landing-subheadline">
            A New Chapter Is Coming.
          </p>
          <p className="mt-6 text-cream/85 text-lg leading-relaxed">
            Queen Denae is preparing the next chapter of the Born Soulful era.
          </p>
          <p className="mt-3 font-serif italic text-lg text-cream/75">
            New music. New stories. A new experience.
          </p>
          <p className="mt-3 text-sand leading-relaxed">
            The official QueenDenae.com experience will return as we prepare for the release of new original music.
          </p>
          <p className="mt-8 font-mono text-xs tracking-[0.5em] uppercase text-gold" data-testid="landing-coming-soon">
            Coming Soon.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.7, duration: 0.8 }}
          className="mt-12 w-full max-w-xl border border-gold/25 bg-obsidian/70 backdrop-blur-md p-7 sm:p-10 text-left"
          data-testid="landing-royal-list"
        >
          <h2 className="font-serif text-2xl sm:text-3xl text-cream tracking-tight text-center">JOIN THE ROYAL LIST</h2>
          <p className="mt-3 text-sm text-sand text-center leading-relaxed">
            Be the first to hear the new music, receive exclusive updates, and know when the next chapter begins.
          </p>
          <div className="mt-6">
            <RoyalListForm source="archive-landing" />
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2 }}
        className="relative z-10 pb-8 px-5 flex flex-col items-center gap-4"
      >
        {socials.length > 0 && (
          <div className="flex items-center gap-4">
            {socials.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                data-testid={s.testid}
                className="w-10 h-10 rounded-full border border-gold/25 flex items-center justify-center text-sand hover:text-obsidian hover:bg-gold hover:border-gold transition-colors duration-300"
              >
                <s.icon size={15} />
              </a>
            ))}
          </div>
        )}
        <p className="text-[11px] text-sand/50">© {new Date().getFullYear()} Queen Denae / Stunner Media and Entertainment</p>
        <Link to="/admin" data-testid="landing-owner-login" className="text-[10px] text-sand/30 hover:text-gold transition-colors inline-flex items-center gap-1">
          <KeyRound size={10} /> Owner Login
        </Link>
      </motion.div>
    </div>
  );
}
